/**
 * Shared types and constants for the translations library.
 */

export type Locale = "pl" | "en" | "uk"

export const DEFAULT_LOCALE: Locale = "pl"

export const SUPPORTED_LOCALES: readonly Locale[] = ["pl", "en", "uk"] as const

/**
 * A count-dependent string in every CLDR plural category the supported
 * locales use. Polish and Ukrainian genuinely need `one` / `few` / `many`
 * (2 pliki vs 5 plików); English repeats `other` in the slots it never
 * selects. All four are required so a missing form is a compile error —
 * translation parity here is enforced by `tsc` alone, not by a linter.
 *
 * `{count}` in the chosen form is replaced with the number.
 *
 * NOTE: keep plural leaves OUT of `apiErrors.*`. The server-side resolver
 * (`localized.ts`) returns the key verbatim for any non-string leaf.
 */
export interface PluralForms {
	one: string
	few: string
	many: string
	other: string
}

/**
 * Copy for a project media collection (Inspirations, Visualizations). Both
 * collections render the same screens and the same add-modal, so they share
 * one shape and differ only in wording (and in the size named by `fileHint`
 * / `errorTooLarge`).
 */
export interface ProjectMediaCopy {
	emptyTitle: string
	emptyBody: string
	addCta: string
	processing: string
	modalTitle: string
	tabFile: string
	tabLink: string
	chooseFile: string
	dropHere: string
	fileHint: string
	urlLabel: string
	urlPlaceholder: string
	titleLabel: string
	descriptionLabel: string
	add: string
	adding: string
	cancel: string
	download: string
	delete: string
	deleteTitle: string
	deleteBody: string
	close: string
	errorUnsupported: string
	errorTooLarge: string
	errorUpload: string
	errorLoad: string
	/** Bottom-right uploader caption on a grid item (ZPL-265). `{name}`. */
	uploadedByLabel: string
}

/**
 * Copy for the multi-file add modal shared by Inspirations and
 * Visualizations. Collection-neutral on purpose — the collection's own
 * `ProjectMediaCopy` supplies the identity strings (title, hints, limits),
 * this block supplies everything about *the batch*.
 */
export interface ProjectMediaUploadCopy {
	/** Heading above the picked-file list. `{count}`. */
	filesSelected: PluralForms
	/** Submit button. `{count}`. */
	addFiles: PluralForms
	/** Submit button while uploading. `{count}`. */
	addingFiles: PluralForms
	/** Summary of files rejected before they entered the list. `{count}`. */
	filesSkipped: PluralForms
	/** Summary after a partly failed batch. `{count}`. */
	filesFailed: PluralForms
	/** Adds more files to an already-populated list. */
	addAnother: string
	/** Accessible name of a row's remove control. `{name}`. */
	removeFile: string
	/** Re-runs only the rows that failed. */
	retryFailed: string
	/** Per-row status labels. */
	statusUploading: string
	statusDone: string
	statusFailed: string
	/** Refused because the same file is already picked. */
	errorDuplicate: string
	/** Refused because the list is already at the per-batch cap. `{max}`. */
	errorBatchLimit: string
	/** Refused because the project would exceed its item quota. `{max}`. */
	errorQuotaFull: string
}

export interface Translations {
	/** App name / brand */
	appName: string

	/** Hero section */
	hero: {
		sentences: string[]
	}

	/** Language switcher */
	language: {
		label: string
		pl: string
		en: string
		uk: string
	}

	/** Generic actions and states */
	common: {
		loading: string
	}

	/** Authentication & User actions */
	auth: {
		login: string
		logout: string
		googleLogin: string
		getStarted: string
		backToMain: string
	}

	/** Dashboard */
	dashboard: {
		title: string
		welcome: string
		dummyText: string
	}

	/** Properties feature */
	properties: {
		newPropertyCta: string
		addFirstPropertyCta: string
		emptyStateTitle: string
		emptyStateBody: string
		lastOpenedLabel: string
		propertiesListHeading: string
		types: {
			apartment: string
			house: string
			office: string
			other: string
		}
		tabs: {
			navLabel: string
			overview: string
			projects: string
			tickets: string
			journal: string
			documents: string
			budget: string
		}
		form: {
			newPageTitle: string
			nameLabel: string
			namePlaceholder: string
			typeLabel: string
			customTypeLabel: string
			customTypePlaceholder: string
			descriptionLabel: string
			descriptionPlaceholder: string
			submit: string
			cancel: string
			errors: {
				nameRequired: string
				nameTooLong: string
				typeRequired: string
				customTypeRequired: string
				customTypeTooLong: string
				descriptionTooLong: string
				unknown: string
			}
		}
		detail: {
			breadcrumbsNavLabel: string
			manageLabel: string
			manageMenu: {
				edit: string
				transfer: string
			}
		}
		edit: {
			title: string
			nameLabel: string
			descriptionLabel: string
			save: string
			saving: string
			cancel: string
			readOnlyNotice: string
			/** In-place editing (ZPL-186). */
			improveWithAi: string
			addDescription: string
			editName: string
			editDescription: string
			descriptionPlaceholder: string
			deleteSectionTitle: string
			deleteSectionBody: string
			deleteAction: string
			deleteConfirmTitle: string
			deleteConfirmBody: string
			deleteConfirmAction: string
			deleteError: string
		}
		transfer: {
			title: string
			explainer: string
			memberLabel: string
			emptyState: string
			confirmationLabel: string
			confirmationPlaceholder: string
			submit: string
			submitting: string
			cancel: string
		}
		share: {
			buttonLabel: string
			/** Divider between the email-invite form and the QR option. */
			or: string
			modalTitle: string
			invite: {
				emailLabel: string
				emailPlaceholder: string
				roleLabel: string
				submit: string
				submitting: string
				confirm: {
					title: string
					body: string
					send: string
					cancel: string
				}
				errors: {
					emailInvalid: string
					selfInvite: string
					alreadyMember: string
					unknown: string
				}
			}
			members: {
				heading: string
				ownerLabel: string
				remove: {
					button: string
					submitting: string
					confirm: {
						title: string
						body: string
						remove: string
						cancel: string
					}
					errors: {
						unknown: string
					}
				}
				role: {
					button: string
					errors: {
						unknown: string
					}
				}
			}
			invitations: {
				heading: string
				emptyState: string
				expiresIn: string
				expiresToday: string
				menu: {
					actionsLabel: string
				}
				remove: {
					button: string
					submitting: string
					confirm: {
						title: string
						body: string
						remove: string
						cancel: string
					}
					errors: {
						unknown: string
					}
				}
				resend: {
					button: string
					submitting: string
					confirm: {
						title: string
						body: string
						send: string
						cancel: string
					}
					errors: {
						unknown: string
					}
				}
				role: {
					button: string
					errors: {
						unknown: string
					}
				}
			}
			roles: {
				member: string
				contractor: string
				architect: string
			}
			roleDescriptions: {
				member: string
				contractor: string
				architect: string
			}
			qr: {
				generateButton: string
				dialogTitle: string
				pinLabel: string
				instructions: string
				instructionsNoPin: string
				expiresInMmSs: string
				expired: string
				done: string
				generating: string
				error: string
			}
		}
	}

	projects: {
		list: {
			heading: string
			createCta: string
			emptyStateTitle: string
			emptyStateBody: string
			archivedHeading: string
			archivedBadge: string
		}
		statuses: {
			draft: string
			ready_for_work: string
			in_progress: string
			awaiting_acceptance: string
			accepted: string
		}
		types: {
			garden: string
			construction: string
			interior: string
		}
		tabs: {
			navLabel: string
			details: string
			tasks: string
			visualizations: string
			visualizationsShort: string
			inspirations: string
			documents: string
			budget: string
			members: string
		}
		form: {
			createTitle: string
			nameLabel: string
			namePlaceholder: string
			typeLabel: string
			descriptionLabel: string
			descriptionPlaceholder: string
			submit: string
			cancel: string
			errors: {
				nameRequired: string
				/** Template: {max} */
				nameTooLong: string
				typeRequired: string
				/** Template: {max} */
				descriptionTooLong: string
				costInvalid: string
				unknown: string
			}
		}
		detail: {
			statusLabel: string
			acceptedOwnerOnlyHint: string
			membersHeading: string
			addMemberCta: string
			addMemberTitle: string
			addMemberEmpty: string
			removeMemberLabel: string
			archiveCta: string
			unarchiveCta: string
			archivedNotice: string
			editCta: string
			editTitle: string
			save: string
			saving: string
			cancel: string
			estimatedExecutionCostLabel: string
			costPlaceholder: string
			currencyLabel: string
			noDescription: string
			descriptionHeading: string
			journalHeading: string
		}
		cost: {
			heading: string
			noCost: string
			acceptedLabel: string
			pendingLabel: string
			proposeCta: string
			proposeTitle: string
			amountLabel: string
			amountPlaceholder: string
			currencyLabel: string
			submit: string
			cancel: string
			acceptCta: string
			rejectCta: string
			markPaidCta: string
			markPaidHint: string
			paidBadge: string
			errors: {
				amountInvalid: string
				unknown: string
			}
		}
		visualizations: ProjectMediaCopy
		inspirations: ProjectMediaCopy
		/** The multi-file add modal shared by both media collections. */
		mediaUpload: ProjectMediaUploadCopy
		/** Property budget tab (ZPL-224) — capture-first Budget, owner-only. */
		budget: {
			title: string
			spentLabel: string
			/** {count} confirmed entries excluded from the sum (no FX rate). */
			excludedNote: string
			draftsLabel: string
			emptyTitle: string
			emptyBody: string
			addCta: string
			captureCta: string
			formTitleAdd: string
			formTitleEdit: string
			amountLabel: string
			currencyLabel: string
			kindLabel: string
			kindNone: string
			vendorLabel: string
			descriptionLabel: string
			occurredAtLabel: string
			projectLabel: string
			projectNone: string
			save: string
			cancel: string
			amountInvalid: string
			kinds: {
				material: string
				labor: string
				service: string
				equipment: string
				mix: string
				other: string
			}
			statuses: {
				ai_draft: string
				confirmed: string
				rejected: string
			}
			accept: string
			reject: string
			edit: string
			delete: string
			deleteConfirmBody: string
			captureModalTitle: string
			captureHint: string
			fileLabel: string
			capture: string
			capturing: string
			duplicateWarning: string
			extractionPending: string
			proofLabel: string
			errorLoad: string
			errorSave: string
			errorFileTooLarge: string
		}
		documents: {
			emptyTitle: string
			emptyBody: string
			emptyBodyProperty: string
			addCta: string
			uploadModalTitle: string
			titleLabel: string
			titlePlaceholder: string
			fileLabel: string
			aiReadableLabel: string
			upload: string
			uploading: string
			cancel: string
			delete: string
			/** ZPL-334 confirm dialog: {title} in the body is the document title. */
			deleteTitle: string
			deleteBody: string
			deleteConfirm: string
			download: string
			open: string
			scopeProject: string
			scopeProperty: string
			allInProperty: string
			scopeFieldLabel: string
			/** Upload-modal document-type selector (ZPL-263). Immutable after upload;
			 *  "contract" and "land_report" (ZPL-331) each trigger their own AI review. */
			documentTypeLabel: string
			documentTypeOptions: {
				contract: string
				agreement: string
				notice: string
				bill: string
				land_report: string
				other: string
			}
			fileDropHint: string
			grantsModalTitle: string
			grantsCta: string
			ownerLabel: string
			grantToggleGrant: string
			grantToggleRevoke: string
			errorLoad: string
			errorFileTooLarge: string
			errorUpload: string
			editCta: string
			editModalTitle: string
			save: string
			errorDownload: string
			errorDownloadBlocked: string
			/** Help text under the "let AI read this" toggle (upload/edit). */
			aiReadableHint: string
			/** Upload-modal notices when the picked file can't be AI-reviewed. */
			aiUploadNoticeUnsupported: string
			/** Over-limit notices. Template: {size} (derived from AI_REVIEW_MAX_BYTES). */
			aiUploadNoticeTooLarge: string
			aiEditNoticeTooLarge: string
			/** Per-document review badge labels + their hover tooltips. */
			aiBadgeReviewing: string
			aiBadgeReviewingHint: string
			aiBadgeReview: string
			aiBadgeReviewHint: string
			aiBadgeFailed: string
			aiBadgeFailedHint: string
			aiBadgeUnsupported: string
			aiBadgeUnsupportedHint: string
			aiBadgeTooLarge: string
			aiBadgeTooLargeHint: string
			/** Document AI-review modal (contract + land report). */
			aiReviewModalTitle: string
			aiReviewProcessing: string
			aiReviewFailed: string
			aiReviewFailedNotContract: string
			aiReviewFailedNotLandReport: string
			aiReviewFailedUnreadable: string
			aiReviewSummary: string
			aiReviewCritical: string
			aiReviewAdvantages: string
			aiReviewDisadvantages: string
			aiReviewMissing: string
			aiReviewPossible: string
			aiReviewAbusive: string
			aiReviewNoFindings: string
			aiReviewDisclaimer: string
			close: string
		}
	}

	/** Invitation email sent to invited collaborators */
	inviteEmail: {
		subject: string
		greeting: string
		body: string
		cta: string
		signature: string
		expiryNote: string
	}

	/** Email sent ~1h after an unacknowledged chat @-mention (ZPL-150).
	 *  Templates: {byDisplayName}, {propertyName}. */
	mentionEmail: {
		subject: string
		greeting: string
		body: string
		cta: string
		signature: string
	}

	/** Email to the property owner when someone accepts their invitation.
	 *  Body templates: {accepterName}, {propertyName}, {role}. */
	invitationAcceptedEmail: {
		subject: string
		greeting: string
		body: string
		signature: string
	}

	/** Daily activity digest email (ZPL-287). Subject template: {date}. */
	activityDigestEmail: {
		subject: string
		intro: string
		openJournal: string
	}

	/** Document shared emails (ZPL — document share notify). Templates:
	 *  {actor}, {title}, {grantee}. */
	documentSharedEmail: {
		granteeSubject: string
		granteeBody: string
		ownerSubject: string
		ownerBody: string
		cta: string
	}

	/** Public accept-invitation page (lands here from the email magic link) */
	acceptInvitation: {
		/** Headline. Templates: {inviterName}, {propertyName}, {role}. */
		title: string
		loadingState: string
		terminalErrors: {
			notFound: string
			expired: string
			alreadyAccepted: string
		}
		/** Template: {invitationEmail} */
		emailMismatch: string
		/** Template: {invitationEmail} */
		signInPrompt: string
		accept: string
		accepting: string
		/** Template: {propertyName} */
		success: string
		backToDashboard: string
		/** CTA into the app shown on terminal/error screens (signed-out visitors
		 *  are routed on to sign-in by the dashboard's auth guard). */
		goToApp: string
		/** Shown when the inviter has no display name and the invitation
		 *  has no recipient email to fall back on (QR-kind). */
		unknownInviter: string
		/** QR-kind invitations land on the web only when scanned with a
		 *  browser / desktop. Acceptance happens on mobile. Templates:
		 *  {propertyName}. */
		qrOpenInAppTitle: string
		qrOpenInAppBody: string
	}

	/** AI response status indicator (shown on property-detail) */
	aiIndicator: {
		processing: string
		processed: string
		failed: string
	}

	/** Authenticated catch-all when a URL matches no in-app route */
	notFound: {
		title: string
	}

	/** Property chat widget */
	chat: {
		headerName: string
		headerSubtitle: string
		inputPlaceholder: string
		emptyState: string
		sendButtonLabel: string
		openButtonLabel: string
		minimizeButtonLabel: string
		expandButtonLabel: string
		collapseButtonLabel: string
		thinkingLabel: string
		unreadBadge: {
			cap: string
			label: string
		}
		shared: {
			headerName: string
			propertyHeaderName: string
			projectHeaderName: string
			composerPlaceholder: string
			mentionSuggestionsLabel: string
			emptyState: string
			sendButtonLabel: string
			closeLabel: string
			memberCountSingular: string
			memberCountPlural: string
			deletedUser: string
		}
		errors: {
			sendFailed: string
			loadFailed: string
		}
		mention: {
			/** Template: {name} (the person who mentioned you) */
			toastTitle: string
		}
	}

	/**
	 * Server-authored Mareczek messages inserted into chat threads at
	 * lifecycle events. Each template carries placeholders rendered with
	 * `String.replace` — no runtime ICU. Authored in the property owner's
	 * locale (best single pick for the shared chat).
	 */
	mareczek: {
		greetings: {
			/** First message in a freshly-created property's private chat. */
			propertyCreated: string // {ownerName}, {propertyName}
			/** First message in a newly-joined property's private chat (invitee). */
			propertyJoined: string // {memberName}, {propertyName}
		}
		systemMessages: {
			/** Posted to shared chat when a new member accepts an invitation. */
			memberJoined: string // {fullName}, {role}
		}
		agentActions: {
			/** Outcome post after the user accepts a proposed agent action. */
			executed: string
			/** Executed post carrying the result's key fact (ZPL-296): "Done — {summary}". */
			executedSummary: string
			/** Outcome post when the accepted action's story returned an error. */
			failed: string
			/** Outcome post after the user declines a proposed agent action. */
			declined: string
			/** Bubble posted when an agent chain died permanently (ZPL-294). */
			chainFailed: string
			/** The approval card UI (ZPL-123). */
			card: {
				titles: {
					"create-project": string
					"update-project": string
					"propose-cost": string
					"archive-project": string
					"invite-member": string
					"resend-invitation": string
					"create-ticket": string
					"update-ticket": string
					"assign-ticket": string
					"set-ticket-status": string
					"set-ticket-reminder": string
					"add-ticket-comment": string
					"delete-ticket": string
					"delete-ticket-comment": string
					"remove-ticket-attachment": string
					"rename-property": string
					"update-property-description": string
					"create-inspiration-from-url": string
					"create-visualization-from-url": string
					"create-budget-entry": string
					"update-budget-entry": string
					"delete-budget-entry": string
					/** Fallback for tool names this client doesn't know yet. */
					generic: string
				}
				accept: string
				decline: string
				statusAccepted: string
				statusDeclined: string
				statusExpired: string
				/** Shown to non-owners while the card is pending (ZPL-295). */
				statusAwaitingOwner: string
			}
		}
	}

	/**
	 * Property journal (ZPL-103). Empty / error / loading copy plus per-event
	 * label + aria templates rendered client-side by the web journal tab.
	 * `events.*` aria strings carry `{actor}` (and `{role}` for `memberJoined`)
	 * placeholders interpolated at render time. `deletedUser` is the GDPR
	 * fallback when the API returns `actor:{userId:null,displayName:null}`.
	 */
	/** Media detail modal navigation (ZPL-267) — shared by inspirations
	 *  and visualizations. */
	/** Persisted-notification bell (ZPL-310). Templates: {actor}, {title}. */
	notificationsBell: {
		title: string
		close: string
		empty: string
		markAllRead: string
		chatMention: string
		ticketAssigned: string
		joinRequestCreated: string
		agentActionPending: string
	}

	mediaModal: {
		prev: string
		next: string
	}

	journal: {
		emptyTitle: string
		emptyDescription: string
		errorTitle: string
		errorRetry: string
		deletedUser: string
		/** Label shown as the source marker on PROPERTY-LEVEL rows in the
		 *  mixed property rollup; project-scoped views render no markers. */
		sourceProperty: string
		events: {
			propertyCreated: { label: string; aria: string }
			memberInvitedEmail: { label: string; aria: string }
			memberInvitedQr: { label: string; aria: string }
			memberJoined: { label: string; aria: string }
			unknown: { label: string; aria: string }
			/** Project-scoped journal events (ZPL-115) */
			projectCreated: { label: string; aria: string }
			/** Templates: {from}, {to} */
			projectStatusChanged: { label: string; aria: string }
			/** Templates: {amount}, {currency} */
			projectCostProposed: { label: string; aria: string }
			/** Templates: {amount}, {currency} */
			projectCostAccepted: { label: string; aria: string }
			/** Templates: {amount}, {currency} */
			projectCostRejected: { label: string; aria: string }
			/** Templates: {amount}, {currency} */
			projectPaid: { label: string; aria: string }
			/** Template: {target} */
			projectMemberAdded: { label: string; aria: string }
			/** Template: {target} */
			projectMemberRemoved: { label: string; aria: string }
			projectArchived: { label: string; aria: string }
			projectUnarchived: { label: string; aria: string }
			/** Template (aria): {actor} */
			inspirationCreated: { label: string; aria: string }
			/** Template (aria): {actor} */
			inspirationDeleted: { label: string; aria: string }
			/** Template (aria): {actor} */
			visualizationCreated: { label: string; aria: string }
			/** Template (aria): {actor} */
			visualizationDeleted: { label: string; aria: string }
			/** Template (aria): {actor} */
			documentCreated: { label: string; aria: string }
			/** Template (aria): {actor} */
			documentDeleted: { label: string; aria: string }
			/** Template (aria): {actor} */
			documentUpdated: { label: string; aria: string }
			/** Templates: {title}; (aria) {actor} */
			ticketCreated: { label: string; aria: string }
			/** Templates: {title}; (aria) {actor} */
			ticketCreatedDefect: { label: string; aria: string }
			/** Templates: {title}, {from}, {to}; (aria) {actor} */
			ticketStatusChanged: { label: string; aria: string }
			/** Templates: {title}, {assignee}; (aria) {actor} */
			ticketAssigned: { label: string; aria: string }
			/** Templates: {title}; (aria) {actor} */
			ticketUnassigned: { label: string; aria: string }
		}
	}

	tickets: {
		emptyTitle: string
		emptyDescription: string
		emptyFilteredTitle: string
		emptyFilteredDescription: string
		errorTitle: string
		errorRetry: string
		createCta: string
		createTitle: string
		titleLabel: string
		titlePlaceholder: string
		kindLabel: string
		assigneeSelectLabel: string
		submitCreate: string
		creating: string
		statusLabel: string
		assignChangeError: string
		statusChangeError: string
		loadMore: string
		loadingMore: string
		boardAriaLabel: string
		/** Kanban|List view switcher (ZPL-264). */
		viewSwitcherLabel: string
		viewBoard: string
		viewList: string
		listAriaLabel: string
		unassigned: string
		dueLabel: string
		createdLabel: string
		updatedLabel: string
		assigneeLabel: string
		reminderLabel: string
		reminderSave: string
		reminderClear: string
		reminderChangeError: string
		descriptionTitle: string
		noDescription: string
		backToList: string
		backToMyTasks: string
		backToProjectTasks: string
		attachmentsTitle: string
		noAttachments: string
		attachmentUpload: string
		attachmentUploading: string
		attachmentOpen: string
		attachmentDelete: string
		attachmentUploadError: string
		attachmentDeleteError: string
		commentsTitle: string
		commentsEmpty: string
		commentPlaceholder: string
		commentSubmit: string
		commentSubmitting: string
		commentCreateError: string
		commentEdit: string
		commentSave: string
		commentCancel: string
		commentEditError: string
		commentDelete: string
		commentDeleteError: string
		commentEdited: string
		mentionSuggestionsLabel: string
		myTicketsTitle: string
		myTicketsEmptyTitle: string
		myTicketsEmptyDescription: string
		myTicketsFilteredEmptyTitle: string
		myTicketsFilteredEmptyDescription: string
		myTicketsAllStatuses: string
		filtersLabel: string
		filterProperty: string
		filterProject: string
		filterAssignee: string
		filterTopic: string
		filterKind: string
		filterAllProperties: string
		filterAllProjects: string
		filterAllAssignees: string
		filterAllTopics: string
		filterAllKinds: string
		filterClear: string
		statusCountLabel: string
		topicUngrouped: string
		topicManage: string
		topicCreate: string
		topicRename: string
		topicDelete: string
		topicDeleteConfirm: string
		topicNameLabel: string
		topicNamePlaceholder: string
		topicScopeLabel: string
		topicScopeProperty: string
		topicCreateError: string
		topicUpdateError: string
		topicDeleteError: string
		status: {
			open: string
			in_progress: string
			done: string
			cancelled: string
		}
		kind: {
			task: string
			defect: string
		}
		formErrors: {
			titleRequired: string
			titleTooLong: string
			descriptionTooLong: string
			unknown: string
		}
	}

	/** Localized labels for the user's main role (set in onboarding, shown in sidebar) */
	userMainRoles: {
		investor: string
		contractor: string
		architect: string
	}

	/**
	 * User-facing 4xx error messages emitted by the API. Keyed by domain
	 * → reason. Internal 5xx strings stay in English at the throw site.
	 */
	apiErrors: {
		auth: {
			unauthorized: string
			forbidden: string
			onboardingRequired: string
			emailRequired: string
			emailAlreadyAssociated: string
		}
		validation: {
			failed: string
		}
		user: {
			notSynced: string
		}
		notifications: {
			notFound: string
			cursorInvalid: string
		}
		property: {
			notFound: string
			forbidden: string
			transferConfirmationMismatch: string
			transferSelf: string
			transferNotAMember: string
		}
		projects: {
			notFound: string
			forbidden: string
			constructionRequiresHouse: string
			statusAcceptedOwnerOnly: string
			memberNotEligible: string
			memberAlreadyAdded: string
			memberNotFound: string
			proposalNotFound: string
			proposalAlreadyDecided: string
			proposalConflict: string
			proposalsLockedPaid: string
			paidRequiresAcceptedCost: string
			alreadyPaid: string
		}
		agentActions: {
			notFound: string
			alreadyResolved: string
			expired: string
		}
		inspirations: {
			notFound: string
			forbidden: string
			unsupportedContentType: string
			fileTooLarge: string
			quotaExceeded: string
			uploadUrlFailed: string
			uploadNotPending: string
			uploadObjectMissing: string
			uploadInvalid: string
			storageError: string
			noFields: string
		}
		visualizations: {
			notFound: string
			forbidden: string
			unsupportedContentType: string
			fileTooLarge: string
			quotaExceeded: string
			uploadUrlFailed: string
			uploadNotPending: string
			uploadObjectMissing: string
			uploadInvalid: string
			storageError: string
			noFields: string
		}
		invitation: {
			notFound: string
			alreadyAccepted: string
			expired: string
			selfInvite: string
			alreadyMember: string
			/** Template: {email} */
			emailMismatch: string
			wrongPin: string
			resendQrUnsupported: string
		}
		membership: {
			alreadyMember: string
			notFound: string
			cannotRemoveOwner: string
			cannotChangeOwnerRole: string
		}
		realtime: {
			channelForbidden: string
			unsupportedChannel: string
		}
		chat: {
			forbidden: string
			acknowledgmentInvalid: string
			participantRequired: string
		}
		fcm: {
			tokenNotFound: string
		}
		journal: {
			cursorInvalid: string
			limitOutOfRange: string
		}
		tickets: {
			notFound: string
			forbidden: string
			projectScopeMismatch: string
			topicScopeMismatch: string
			assigneeNotMember: string
			invalidTransition: string
			cursorInvalid: string
			limitOutOfRange: string
			statusConflict: string
		}
		ticketComments: {
			notFound: string
			forbidden: string
		}
		ticketAttachments: {
			notFound: string
			forbidden: string
			unsupportedContentType: string
			fileTooLarge: string
			validationFailed: string
		}
		mediaUploads: {
			notFound: string
			forbidden: string
			propertyRequired: string
			unsupportedContentType: string
			fileTooLarge: string
			validationFailed: string
			objectMissing: string
		}
		documents: {
			notFound: string
			forbidden: string
			unsupportedContentType: string
			fileTooLarge: string
			validationFailed: string
			granteeNotMember: string
			objectMissing: string
		}
		topics: {
			notFound: string
			forbidden: string
			projectScopeMismatch: string
		}
		budgetEntries: {
			notFound: string
			forbidden: string
			projectScopeMismatch: string
			topicScopeMismatch: string
			amountRequired: string
			amountInvalid: string
			invalidStatus: string
			statusConflict: string
			noFields: string
			cursorInvalid: string
			limitOutOfRange: string
			unsupportedContentType: string
			fileTooLarge: string
			uploadUrlFailed: string
			objectMissing: string
			validationFailed: string
			storageError: string
			documentNotReady: string
		}
	}

	/**
	 * Push notification copy. Built by the push-chat-message dispatcher into
	 * FCM payloads; the device-side OS shows it when the app is not foregrounded
	 * on the matching chat. Per-kind titles differentiate private (Mareczek
	 * 1:1) vs shared (multi-member) chats in the system tray.
	 */
	apiPushCopy: {
		chat: {
			/** Template: {property} */
			titlePrivate: string
			/** Template: {property} */
			titleShared: string
			bodyPreviewFallback: string
		}
		/** Journal push bodies (spec §5). Title is always the property name —
		 *  rendered by the dispatcher, not a template here. */
		journal: {
			/** {actor}, {role} */
			memberInvited: string
			/** {actor} */
			memberJoined: string
			/** {actor} */
			propertyCreated: string
			/** {actor}, {project} */
			projectCreated: string
			/** {actor}, {project}, {from}, {to} */
			projectStatusChanged: string
			/** {actor}, {project}, {amount}, {currency} */
			projectCostProposed: string
			/** {actor}, {project}, {amount}, {currency} */
			projectCostAccepted: string
			/** {actor}, {project}, {amount}, {currency} */
			projectCostRejected: string
			/** {actor}, {project}, {amount}, {currency} */
			projectPaid: string
			/** {actor}, {project}, {member} */
			projectMemberAdded: string
			/** {actor}, {project}, {member} */
			projectMemberRemoved: string
			/** {actor}, {project} */
			projectArchived: string
			/** {actor}, {project} */
			projectUnarchived: string
			/** {actor}, {project} */
			inspirationCreated: string
			/** {actor}, {project} */
			inspirationDeleted: string
			/** {actor}, {project} */
			visualizationCreated: string
			/** {actor}, {project} */
			visualizationDeleted: string
			/** {actor} */
			documentCreated: string
			/** {actor} */
			documentDeleted: string
			/** {actor} */
			documentUpdated: string
		}
		property: {
			/** {property} */
			joinApproved: string
			/** {requester} */
			joinRequest: string
		}
		ticket: {
			/** {actor}, {title} */
			created: string
			/** {actor}, {title} */
			createdDefect: string
			/** {actor}, {title} */
			assigned: string
			/** {title}, {status} */
			statusChanged: string
			/** {actor}, {title} */
			statusDone: string
			/** {actor}, {title} */
			statusDoneDefect: string
			/** {title} */
			reminder: string
			/** {title} */
			reminderDefect: string
			/** {actor}, {title} */
			commentMention: string
			/** {actor}, {title} */
			commentAdded: string
			/** {actor}, {title} */
			mention: string
		}
	}

	/** Left sidebar nav (authenticated routes) */
	sidebar: {
		navLabel: string
		properties: string
		myTickets: string
		profile: string
		settings: string
		signOut: string
		more: string
		collapseMenu: string
		expandMenu: string
	}

	/** Settings page (notification preferences) */
	settings: {
		title: string
		notifications: {
			title: string
			masterToggle: string
			quietHoursToggle: string
			quietHoursStart: string
			quietHoursEnd: string
			propertiesTitle: string
			muteProperty: string
			save: string
			savedTimeZone: string
			loading: string
			error: string
		}
	}

	/** Theme switcher (light / dark / auto) */
	theme: {
		light: string
		dark: string
		auto: string
		toggleLabel: string
	}

	/** User profile page */
	profile: {
		title: string
		firstNameLabel: string
		firstNamePlaceholder: string
		lastNameLabel: string
		lastNamePlaceholder: string
		languageLabel: string
		aiContextLabel: string
		aiContextPlaceholder: string
		mainRoleLabel: string
		save: string
		cancel: string
		saved: string
		deleteAccount: {
			sectionTitle: string
			sectionBody: string
			open: string
			dialogTitle: string
			dialogBody: string
			confirmationPhrase: string
			/** {phrase} */
			phraseLabel: string
			confirm: string
			submitting: string
			cancel: string
			error: string
		}
		errors: {
			firstNameRequired: string
			firstNameTooLong: string
			lastNameRequired: string
			lastNameTooLong: string
			aiContextTooLong: string
			mainRoleRequired: string
			unknown: string
		}
	}

	/** Onboarding wizard (modal-only screen on first login) */
	onboarding: {
		welcomeHeading: string
		justOneMoreThing: string
		subtitle: string
		firstNameLabel: string
		firstNamePlaceholder: string
		lastNameLabel: string
		lastNamePlaceholder: string
		languageLabel: string
		mainRoleLabel: string
		mainRolePlaceholder: string
		termsLabel: string
		termsLink: string
		submit: string
		errors: {
			firstNameRequired: string
			firstNameTooLong: string
			lastNameRequired: string
			lastNameTooLong: string
			mainRoleRequired: string
			termsRequired: string
			unknown: string
		}
	}
}
